/**
 * @fileoverview Dedicated MongoDB dashboard — live cluster → database →
 *   collection drill-down for the system cluster and per-environment tenant
 *   data clusters (serverStatus, dbStats, collStats, indexes, Atlas metrics).
 * @module system-web/pages/MongoDbDashboardPage
 *
 * Responsibilities:
 * - Render the cluster list (system + tenant) from useMongoDbMonitoring.
 * - Lazily render per-cluster serverStatus + Atlas node metrics + database list.
 * - Lazily render per-database dbStats + collection list, and per-collection
 *   collStats + index details, on demand as the operator expands each level.
 *
 * Dependencies:
 * - useMongoDbMonitoring (owns all async state + on-demand loading).
 *
 * Architecture:
 * - Local-first EXCEPTION: monitoring is inherently online. This page NEVER
 *   calls fetch/apiClient — all data arrives through the hook, which delegates
 *   to mongoDbMonitoringService.
 *
 * Notes:
 * - Responses never include cluster connection strings or credentials.
 *
 * @component MongoDbDashboardPage
 * Data Source: local (useMongoDbMonitoring → mongoDbMonitoringService).
 * Constraints: no direct API calls; on-demand drill-down only.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import {
  Database,
  Server,
  HardDrive,
  Layers,
  ChevronDown,
  ChevronRight,
  RefreshCw,
  AlertTriangle,
  Activity,
  ArrowLeft,
  Cpu,
  KeyRound,
} from 'lucide-react';
import {
  useMongoDbMonitoring,
  dbKey,
  collKey,
  type AsyncState,
} from '../hooks/useMongoDbMonitoring';
import type {
  ClusterDetail,
  DatabaseDetail,
  CollectionDetail,
  AtlasNode,
} from '../services/mongoDbMonitoringService';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined) return 'n/a';
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(k)), sizes.length - 1);
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
}
function fmtNum(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------
function StatCard({ label, value, sub, icon: Icon }: { label: string; value: string; sub?: string; icon: React.ElementType }) {
  return (
    <div className="flex-1 min-w-0 rounded-lg p-3 border" style={{ borderColor: 'rgba(0,0,0,0.08)', background: 'rgba(0,0,0,0.02)' }}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="h-3.5 w-3.5 opacity-50" />
        <span className="text-xs opacity-60 font-medium uppercase tracking-wide">{label}</span>
      </div>
      <p className="text-lg font-bold tabular-nums truncate">{value}</p>
      {sub && <p className="text-xs opacity-50 mt-0.5">{sub}</p>}
    </div>
  );
}

function ErrorNote({ message }: { message: string }) {
  return (
    <div className="flex items-start gap-2 px-4 py-3 text-sm" style={{ color: '#b45309' }}>
      <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
      <span>{message}</span>
    </div>
  );
}

function Spinner({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-2 px-4 py-3 text-sm opacity-60">
      <RefreshCw className="h-4 w-4 animate-spin" />
      {label}
    </div>
  );
}

function AtlasNodeRow({ node }: { node: AtlasNode }) {
  const isPrimary = node.role === 'PRIMARY';
  const cpuColor = node.cpuPercent > 80 ? '#dc2626' : node.cpuPercent > 60 ? '#d97706' : '#16a34a';
  return (
    <div className="flex items-center gap-4 px-4 py-2 border-b last:border-0 text-sm" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
      <span className="w-20 font-medium text-xs px-2 py-0.5 rounded" style={{
        background: isPrimary ? 'rgba(245,158,11,0.15)' : 'rgba(0,0,0,0.06)',
        color: isPrimary ? '#92400e' : 'inherit',
      }}>{node.role}</span>
      <span className="text-xs opacity-60 truncate flex-1">{node.host}</span>
      <span className="tabular-nums font-semibold" style={{ color: cpuColor }}>{node.cpuPercent.toFixed(1)}% CPU</span>
      <span className="tabular-nums opacity-60">{node.memPercent.toFixed(1)}% mem</span>
      <span className="tabular-nums opacity-60">{node.diskUsedPercent.toFixed(1)}% disk</span>
      <span className="tabular-nums opacity-60">lag {node.replicationLagSeconds}s</span>
    </div>
  );
}

/**
 * @component CollectionSection
 * Purpose:  Per-collection collStats + index details (deepest drill level).
 * Props:    state (async collection detail), expanded, onToggle, name.
 * Behavior: Loads on expand; renders index rows + stat cards; surfaces errors.
 */
function CollectionSection({
  name,
  expanded,
  state,
  onToggle,
}: {
  name: string;
  expanded: boolean;
  state: AsyncState<CollectionDetail> | undefined;
  onToggle: () => void;
}) {
  return (
    <div className="border-b last:border-0" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
      <button onClick={onToggle} className="w-full flex items-center gap-2 px-4 py-2 text-left text-sm">
        {expanded ? <ChevronDown className="h-3.5 w-3.5 opacity-40" /> : <ChevronRight className="h-3.5 w-3.5 opacity-40" />}
        <Layers className="h-3.5 w-3.5 opacity-50" />
        <span className="font-medium truncate">{name}</span>
      </button>
      {expanded && (
        <div className="pl-8 pr-4 pb-3">
          {state?.loading && <Spinner label="Loading collection stats…" />}
          {state?.error && <ErrorNote message={state.error} />}
          {state?.data?.error && !state.error && <ErrorNote message={state.data.error} />}
          {state?.data?.stats && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
                <StatCard label="Documents" value={fmtNum(state.data.stats.documents)} icon={Database} />
                <StatCard label="Data size" value={formatBytes(state.data.stats.dataSize)} icon={HardDrive} />
                <StatCard label="Storage" value={formatBytes(state.data.stats.storageSize)} icon={HardDrive} />
                <StatCard label="Index size" value={formatBytes(state.data.stats.totalIndexSize)} icon={KeyRound} />
              </div>
              <p className="text-xs opacity-50 mb-1 flex items-center gap-1.5">
                <KeyRound className="h-3.5 w-3.5" /> {state.data.indexes.length} index(es)
              </p>
              <div className="rounded-lg border overflow-hidden" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
                {state.data.indexes.map((idx) => (
                  <div key={idx.name} className="flex items-center gap-3 px-3 py-1.5 border-b last:border-0 text-xs" style={{ borderColor: 'rgba(0,0,0,0.05)' }}>
                    <span className="font-medium w-40 truncate">{idx.name}</span>
                    <span className="opacity-60 flex-1 truncate">{JSON.stringify(idx.keys)}</span>
                    {idx.unique && <span className="px-1.5 py-0.5 rounded" style={{ background: 'rgba(99,102,241,0.1)', color: '#4f46e5' }}>unique</span>}
                    <span className="tabular-nums opacity-50">{formatBytes(idx.sizeBytes)}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * @component DatabaseSection
 * Purpose:  Per-database dbStats + collection list with per-collection drill.
 * Props:    clusterId, name, expanded, state, expandedCollections, toggles.
 * Behavior: Loads dbStats/collections on expand; nests CollectionSection.
 */
function DatabaseSection({
  clusterId,
  name,
  expanded,
  state,
  expandedCollections,
  collectionDetails,
  onToggle,
  onToggleCollection,
}: {
  clusterId: string;
  name: string;
  expanded: boolean;
  state: AsyncState<DatabaseDetail> | undefined;
  expandedCollections: Set<string>;
  collectionDetails: Record<string, AsyncState<CollectionDetail>>;
  onToggle: () => void;
  onToggleCollection: (collName: string) => void;
}) {
  return (
    <div className="border rounded-lg mb-2 overflow-hidden" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
      <button onClick={onToggle} className="w-full flex items-center gap-2 px-4 py-2.5 text-left text-sm" style={{ background: 'rgba(0,0,0,0.02)' }}>
        {expanded ? <ChevronDown className="h-4 w-4 opacity-40" /> : <ChevronRight className="h-4 w-4 opacity-40" />}
        <Database className="h-4 w-4 opacity-50" />
        <span className="font-semibold flex-1 truncate">{name}</span>
        {state?.data?.stats && <span className="text-xs opacity-50">{fmtNum(state.data.stats.collections)} colls · {formatBytes(state.data.stats.dataSize)}</span>}
      </button>
      {expanded && (
        <div className="px-3 py-3">
          {state?.loading && <Spinner label="Loading database stats…" />}
          {state?.error && <ErrorNote message={state.error} />}
          {state?.data?.error && !state.error && <ErrorNote message={state.data.error} />}
          {state?.data?.stats && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
              <StatCard label="Collections" value={fmtNum(state.data.stats.collections)} icon={Layers} />
              <StatCard label="Documents" value={fmtNum(state.data.stats.documents)} icon={Database} />
              <StatCard label="Data size" value={formatBytes(state.data.stats.dataSize)} icon={HardDrive} />
              <StatCard label="Storage" value={formatBytes(state.data.stats.storageSize)} icon={HardDrive} />
              <StatCard label="Index size" value={formatBytes(state.data.stats.indexSize)} icon={KeyRound} />
              <StatCard label="Indexes" value={fmtNum(state.data.stats.indexes)} icon={KeyRound} />
            </div>
          )}
          {state?.data && state.data.collections.length > 0 && (
            <div className="rounded-lg border overflow-hidden" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
              {state.data.collections.map((coll) => (
                <CollectionSection
                  key={coll.name}
                  name={coll.name}
                  expanded={expandedCollections.has(collKey(clusterId, name, coll.name))}
                  state={collectionDetails[collKey(clusterId, name, coll.name)]}
                  onToggle={() => onToggleCollection(coll.name)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * @component ClusterSection
 * Purpose:  Top-level cluster card — serverStatus, Atlas nodes, database list.
 * Props:    cluster meta + async detail + nested db/collection state + toggles.
 * Behavior: Loads serverStatus/databases/Atlas on expand; nests DatabaseSection.
 */
function ClusterSection({
  clusterId,
  name,
  kind,
  region,
  status,
  expanded,
  state,
  expandedDatabases,
  expandedCollections,
  databaseDetails,
  collectionDetails,
  onToggle,
  onReload,
  onToggleDatabase,
  onToggleCollection,
}: {
  clusterId: string;
  name: string;
  kind: 'system' | 'tenant';
  region: string | null;
  status: string | null;
  expanded: boolean;
  state: AsyncState<ClusterDetail> | undefined;
  expandedDatabases: Set<string>;
  expandedCollections: Set<string>;
  databaseDetails: Record<string, AsyncState<DatabaseDetail>>;
  collectionDetails: Record<string, AsyncState<CollectionDetail>>;
  onToggle: () => void;
  onReload: () => void;
  onToggleDatabase: (dbName: string) => void;
  onToggleCollection: (dbName: string, collName: string) => void;
}) {
  return (
    <div className="rounded-xl border mb-4 overflow-hidden" style={{ borderColor: 'rgba(0,0,0,0.08)', background: 'var(--card-bg, white)' }}>
      <div className="w-full flex items-center gap-3 px-4 py-3" style={{ borderBottom: expanded ? '1px solid rgba(0,0,0,0.06)' : 'none' }}>
        <button onClick={onToggle} className="flex items-center gap-3 flex-1 text-left">
          {expanded ? <ChevronDown className="h-4 w-4 opacity-40" /> : <ChevronRight className="h-4 w-4 opacity-40" />}
          <Server className="h-4 w-4 opacity-60" />
          <span className="font-semibold text-sm">{name}</span>
          <span className="text-xs px-2 py-0.5 rounded" style={{ background: kind === 'system' ? 'rgba(245,158,11,0.12)' : 'rgba(99,102,241,0.1)', color: kind === 'system' ? '#92400e' : '#4f46e5' }}>
            {kind}
          </span>
          {region && <span className="text-xs opacity-50">{region}</span>}
          {status && <span className="text-xs opacity-50">· {status}</span>}
        </button>
        {expanded && (
          <button onClick={onReload} className="text-xs inline-flex items-center gap-1.5 px-2 py-1 rounded-lg border" style={{ borderColor: 'rgba(0,0,0,0.12)' }}>
            <RefreshCw className={`h-3.5 w-3.5 ${state?.loading ? 'animate-spin' : ''}`} />
            Reload
          </button>
        )}
      </div>
      {expanded && (
        <div className="p-4">
          {state?.loading && !state.data && <Spinner label="Loading cluster status…" />}
          {state?.error && <ErrorNote message={state.error} />}
          {state?.data?.error && !state.error && <ErrorNote message={state.data.error} />}

          {state?.data?.serverStatus && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
              <StatCard label="Version" value={state.data.serverStatus.version} sub={`Up ${state.data.serverStatus.uptimeFormatted}`} icon={Database} />
              <StatCard label="Connections" value={String(state.data.serverStatus.connections.current)} sub={`${state.data.serverStatus.connections.available} available`} icon={Server} />
              <StatCard label="Queries" value={fmtNum(state.data.serverStatus.opcounters.query)} sub="opcounter" icon={Activity} />
              <StatCard label="Inserts" value={fmtNum(state.data.serverStatus.opcounters.insert)} sub="opcounter" icon={Activity} />
            </div>
          )}

          {state?.data?.atlas && state.data.atlas.nodes.length > 0 && (
            <div className="mb-4">
              <p className="text-xs opacity-50 mb-1 flex items-center gap-1.5"><Cpu className="h-3.5 w-3.5" /> Atlas nodes — {state.data.atlas.tier} · {state.data.atlas.state}</p>
              <div className="rounded-lg border overflow-hidden" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
                {state.data.atlas.nodes.map((node) => <AtlasNodeRow key={node.host} node={node} />)}
              </div>
            </div>
          )}

          {state?.data && state.data.databases.length > 0 && (
            <div>
              <p className="text-xs opacity-50 mb-1 flex items-center gap-1.5"><Database className="h-3.5 w-3.5" /> {state.data.databases.length} database(s)</p>
              {state.data.databases.map((db) => (
                <DatabaseSection
                  key={db.name}
                  clusterId={clusterId}
                  name={db.name}
                  expanded={expandedDatabases.has(dbKey(clusterId, db.name))}
                  state={databaseDetails[dbKey(clusterId, db.name)]}
                  expandedCollections={expandedCollections}
                  collectionDetails={collectionDetails}
                  onToggle={() => onToggleDatabase(db.name)}
                  onToggleCollection={(collName) => onToggleCollection(db.name, collName)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function MongoDbDashboardPage() {
  const {
    clusters,
    loadingClusters,
    clustersError,
    reloadClusters,
    clusterDetails,
    databaseDetails,
    collectionDetails,
    expandedClusters,
    expandedDatabases,
    expandedCollections,
    toggleCluster,
    toggleDatabase,
    toggleCollection,
    reloadCluster,
  } = useMongoDbMonitoring();

  const systemClusters = clusters.filter((c) => c.kind === 'system');
  const tenantClusters = clusters.filter((c) => c.kind === 'tenant');

  const renderCluster = (c: (typeof clusters)[number]) => (
    <ClusterSection
      key={c.id}
      clusterId={c.id}
      name={c.name}
      kind={c.kind}
      region={c.region}
      status={c.status}
      expanded={expandedClusters.has(c.id)}
      state={clusterDetails[c.id]}
      expandedDatabases={expandedDatabases}
      expandedCollections={expandedCollections}
      databaseDetails={databaseDetails}
      collectionDetails={collectionDetails}
      onToggle={() => toggleCluster(c.id)}
      onReload={() => reloadCluster(c.id)}
      onToggleDatabase={(dbName) => toggleDatabase(c.id, dbName)}
      onToggleCollection={(dbName, collName) => toggleCollection(c.id, dbName, collName)}
    />
  );

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <div className="flex-1">
          <h1 className="text-xl font-bold flex items-center gap-2">
            <Database className="h-5 w-5" style={{ color: '#10b981' }} />
            MongoDB Dashboard
          </h1>
          <p className="text-xs opacity-50 mt-0.5">Live cluster → database → collection drill-down</p>
        </div>
        <Link
          to="/system-status"
          className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border transition-colors"
          style={{ borderColor: 'rgba(0,0,0,0.12)' }}
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          System Status
        </Link>
        <button
          onClick={reloadClusters}
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm border transition-colors"
          style={{ borderColor: 'rgba(0,0,0,0.12)' }}
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loadingClusters ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {loadingClusters && clusters.length === 0 && (
        <div className="p-8 flex items-center justify-center h-40">
          <RefreshCw className="h-6 w-6 animate-spin opacity-40" />
          <span className="ml-3 opacity-60">Loading clusters…</span>
        </div>
      )}

      {clustersError && clusters.length === 0 && (
        <div className="rounded-xl border p-6 flex items-start gap-3" style={{ borderColor: 'rgba(220,38,38,0.3)', background: 'rgba(220,38,38,0.05)' }}>
          <AlertTriangle className="h-5 w-5 mt-0.5" style={{ color: '#dc2626' }} />
          <div>
            <p className="font-semibold text-sm">Failed to load MongoDB clusters</p>
            <p className="text-xs opacity-70 mt-1">{clustersError}</p>
          </div>
          <button onClick={reloadClusters} className="ml-auto text-xs px-3 py-1.5 rounded-lg font-medium" style={{ background: 'rgba(220,38,38,0.1)', color: '#dc2626' }}>Retry</button>
        </div>
      )}

      {systemClusters.length > 0 && (
        <>
          <h2 className="text-sm font-semibold opacity-60 uppercase tracking-wide mb-2">System Cluster</h2>
          {systemClusters.map(renderCluster)}
        </>
      )}

      {tenantClusters.length > 0 && (
        <>
          <h2 className="text-sm font-semibold opacity-60 uppercase tracking-wide mb-2 mt-4">Tenant Data Clusters</h2>
          {tenantClusters.map(renderCluster)}
        </>
      )}

      {!loadingClusters && !clustersError && clusters.length === 0 && (
        <p className="text-sm opacity-50 px-1">No clusters registered for this environment.</p>
      )}
    </div>
  );
}
